import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Sparkles, Coins, Star, Crown, X, ShoppingBag, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import EnhancedCard from './EnhancedCard';

// Rarity visual configs
const RARITY_CONFIG = {
  common: {
    color: '#A0A0A0',
    glowColor: 'rgba(160, 160, 160, 0.6)',
    bgGradient: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
    particles: 20,
    label: 'COMMON',
    emoji: '📦'
  },
  rare: {
    color: '#00CCFF',
    glowColor: 'rgba(0, 204, 255, 0.8)',
    bgGradient: 'linear-gradient(135deg, #001a33 0%, #003366 100%)',
    particles: 40,
    label: 'RARE!',
    emoji: '💎'
  },
  epic: {
    color: '#FF00FF',
    glowColor: 'rgba(255, 0, 255, 0.8)',
    bgGradient: 'linear-gradient(135deg, #1a001a 0%, #330033 100%)',
    particles: 60,
    label: 'EPIC!!',
    emoji: '🔮'
  },
  legendary: {
    color: '#FFD700',
    glowColor: 'rgba(255, 215, 0, 0.9)',
    bgGradient: 'linear-gradient(135deg, #1a1a00 0%, #333300 100%)',
    particles: 100,
    label: 'LEGENDARY!!!',
    emoji: '👑'
  },
  shards: {
    color: '#00FF88',
    glowColor: 'rgba(0, 255, 136, 0.8)',
    bgGradient: 'linear-gradient(135deg, #001a0d 0%, #003319 100%)',
    particles: 30,
    label: 'SHARDS!',
    emoji: '💰'
  }
};

// Particle component for explosion effect
const Particle = ({ delay, config, index }) => {
  const angle = (index / 30) * Math.PI * 2;
  const distance = 100 + Math.random() * 150;
  const randomX = Math.cos(angle) * distance;
  const randomY = Math.sin(angle) * distance;
  const size = Math.random() * 8 + 4;
  
  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size,
        height: size,
        background: config.color,
        boxShadow: `0 0 ${size * 2}px ${config.glowColor}`,
        left: '50%',
        top: '50%',
      }}
      initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
      animate={{
        x: [0, randomX * 0.5, randomX],
        y: [0, randomY * 0.5, randomY],
        scale: [0, 1.5, 0],
        opacity: [0, 1, 0],
      }}
      transition={{
        duration: 1.5,
        delay: delay,
        ease: "easeOut"
      }}
    />
  );
};

// Sound effect helper
const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    if (type === 'legendary') {
      // Epic fanfare
      osc.frequency.setValueAtTime(523, ctx.currentTime); // C5
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.15); // E5
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.3); // G5
      osc.frequency.setValueAtTime(1047, ctx.currentTime + 0.45); // C6
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
      osc.start();
      osc.stop(ctx.currentTime + 0.8);
    } else if (type === 'rare') {
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } else {
      // Common success
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    }
  } catch (e) { /* Audio not available */ }
};

const DailyRewardModal = ({ 
  isOpen, 
  onClose, 
  onClaim, 
  reward = null,
  streak = 0,
  isClaiming = false 
}) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('intro'); // 'intro' | 'opening' | 'reveal'
  const [showParticles, setShowParticles] = useState(false);
  
  // Reset phase when modal opens
  useEffect(() => {
    if (isOpen && !reward) {
      setPhase('intro');
      setShowParticles(false);
    }
  }, [isOpen, reward]);
  
  // Trigger reveal animation when reward arrives
  useEffect(() => {
    if (reward && phase !== 'reveal') {
      setPhase('reveal');
      setShowParticles(true);
      
      // Play sound based on reward type
      if (reward.type === 'card') {
        if (reward.rarity === 'legendary') {
          playSound('legendary');
        } else if (reward.rarity === 'rare' || reward.rarity === 'epic') {
          playSound('rare');
        } else {
          playSound('common');
        }
      } else {
        playSound('common');
      }
    }
  }, [reward, phase]);
  
  const handleClaim = useCallback(() => {
    setPhase('opening');
    onClaim?.();
  }, [onClaim]);
  
  const handleSellOnMarket = useCallback(() => {
    onClose?.();
    navigate('/marketplace');
  }, [onClose, navigate]);
  
  const handleAddToInventory = useCallback(() => {
    onClose?.();
    navigate('/gallery');
  }, [onClose, navigate]);
  
  // Get config based on reward
  const getConfig = () => {
    if (!reward) return RARITY_CONFIG.common;
    if (reward.type === 'shards') return RARITY_CONFIG.shards;
    return RARITY_CONFIG[reward.rarity] || RARITY_CONFIG.common;
  };
  
  const config = getConfig();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black/95"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />
        
        {/* Animated Background */}
        <motion.div
          className="absolute inset-0 overflow-hidden"
          style={{ background: phase === 'reveal' ? config.bgGradient : 'transparent' }}
        >
          {/* Radial glow */}
          {phase === 'reveal' && (
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
              style={{ 
                background: `radial-gradient(circle, ${config.glowColor} 0%, transparent 70%)` 
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.5, 1], opacity: [0, 0.8, 0.5] }}
              transition={{ duration: 1 }}
            />
          )}
        </motion.div>
        
        {/* Particles */}
        {showParticles && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(config.particles)].map((_, i) => (
              <Particle 
                key={i} 
                delay={Math.random() * 0.3} 
                config={config}
                index={i}
              />
            ))}
          </div>
        )}

        {/* FIX: Global Close Button - Always visible */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-3 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-50"
          data-testid="daily-reward-close-btn"
        >
          <X className="w-6 h-6 text-red-400" />
        </button>

        {/* Content Container */}
        <div className="relative z-10 w-full max-w-lg px-4">
          
          {/* INTRO PHASE: Show the gift box */}
          {phase === 'intro' && (
            <motion.div
              className="flex flex-col items-center text-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              {/* Streak Badge */}
              {streak > 1 && (
                <motion.div
                  className="mb-4 px-4 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/50"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="text-yellow-400 font-bold">🔥 {streak} Day Streak!</span>
                </motion.div>
              )}
              
              {/* Title */}
              <motion.h2
                className="font-anton text-4xl md:text-5xl text-white mb-2"
                animate={{ 
                  textShadow: [
                    '0 0 20px rgba(255,215,0,0.5)',
                    '0 0 40px rgba(255,215,0,0.8)',
                    '0 0 20px rgba(255,215,0,0.5)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                FREE DAILY PACK
              </motion.h2>
              <p className="text-white/60 mb-8">Your daily mystery reward awaits!</p>
              
              {/* Animated Gift Box */}
              <motion.div
                className="relative w-48 h-48 mb-8"
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [-2, 2, -2]
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                {/* Glow behind box */}
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: 'radial-gradient(circle, rgba(255,215,0,0.4) 0%, transparent 70%)',
                  }}
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                
                {/* Gift box visual */}
                <div className="relative w-full h-full flex items-center justify-center">
                  <motion.div
                    className="text-[120px]"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      filter: [
                        'drop-shadow(0 0 20px rgba(255,215,0,0.5))',
                        'drop-shadow(0 0 40px rgba(255,215,0,0.8))',
                        'drop-shadow(0 0 20px rgba(255,215,0,0.5))'
                      ]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    🎁
                  </motion.div>
                </div>
              </motion.div>
              
              {/* Claim Button */}
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Button
                  onClick={handleClaim}
                  disabled={isClaiming}
                  className="w-64 h-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black font-anton text-2xl rounded-xl shadow-lg shadow-orange-500/50"
                  data-testid="claim-daily-reward-btn"
                >
                  {isClaiming ? (
                    <><Sparkles className="w-6 h-6 mr-2 animate-spin" /> OPENING...</>
                  ) : (
                    <><Gift className="w-6 h-6 mr-2" /> OPEN PACK</>
                  )}
                </Button>
              </motion.div>
              
              {/* Probability hint */}
              <p className="text-white/30 text-xs mt-4">
                80% Common • 15% Shards • 4.9% Rare • 0.1% Legendary
              </p>
            </motion.div>
          )}
          
          {/* OPENING PHASE: Shaking animation */}
          {phase === 'opening' && (
            <motion.div
              className="flex flex-col items-center"
              initial={{ scale: 1 }}
              animate={{ 
                scale: [1, 1.1, 0.9, 1.2, 0],
                rotate: [-5, 5, -10, 10, 0],
                opacity: [1, 1, 1, 1, 0]
              }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            >
              <motion.div className="text-[150px]">
                🎁
              </motion.div>
            </motion.div>
          )}
          
          {/* REVEAL PHASE: Show the reward */}
          {phase === 'reveal' && reward && (
            <motion.div
              className="flex flex-col items-center text-center"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            >
              {/* Reward Label */}
              <motion.div
                className="mb-4"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <span 
                  className="font-anton text-4xl md:text-5xl"
                  style={{ 
                    color: config.color,
                    textShadow: `0 0 30px ${config.glowColor}`
                  }}
                >
                  {config.emoji} {config.label} {config.emoji}
                </span>
              </motion.div>
              
              {/* Reward Content */}
              {reward.type === 'card' && reward.card && (
                <motion.div
                  className="mb-6"
                  initial={{ rotateY: 180, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <EnhancedCard card={reward.card} showNews={false} />
                </motion.div>
              )}
              
              {reward.type === 'shards' && (
                <motion.div
                  className="mb-6 flex flex-col items-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.4 }}
                >
                  <motion.div
                    className="text-8xl mb-4"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [-5, 5, -5]
                    }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    💎
                  </motion.div>
                  <motion.span
                    className="font-anton text-6xl text-green-400"
                    style={{ textShadow: '0 0 30px rgba(0,255,136,0.6)' }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    +{reward.amount}
                  </motion.span>
                  <span className="text-white/60 text-xl">SHARDS</span>
                </motion.div>
              )}
              
              {/* Streak info */}
              {streak > 0 && (
                <motion.p
                  className="text-yellow-400 text-sm mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  🔥 Login Streak: {streak} days
                </motion.p>
              )}
              
              {/* Action Buttons */}
              <motion.div
                className="flex flex-col gap-3 w-full max-w-xs"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                {reward.type === 'card' && (
                  <>
                    <Button
                      onClick={handleSellOnMarket}
                      className="w-full h-14 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-anton text-lg rounded-xl"
                      data-testid="sell-on-market-btn"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      SELL ON MARKET?
                    </Button>
                    <Button
                      onClick={handleAddToInventory}
                      variant="outline"
                      className="w-full h-12 border-2 border-white/30 text-white hover:bg-white/10 font-anton text-base rounded-xl"
                      data-testid="add-to-inventory-btn"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      ADD TO COLLECTION
                    </Button>
                  </>
                )}
                
                {reward.type === 'shards' && (
                  <Button
                    onClick={onClose}
                    className="w-full h-14 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-anton text-lg rounded-xl"
                    data-testid="close-daily-reward-btn"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    AWESOME!
                  </Button>
                )}
              </motion.div>
              
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-white/50 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DailyRewardModal;
